export type Language = 'en' | 'ps' | 'ur'

export const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About Us',
      membership: 'Membership',
      events: 'Events',
      charity: 'Charity',
      team: 'Team',
      contact: 'Contact',
      joinMembership: 'Join Membership',
      donate: 'Donate',
      language: 'Language',
    },
    common: {
      learnMore: 'Learn More',
      viewProjects: 'View Projects',
      seeEvents: 'See Events',
      contactUs: 'Contact Us',
      readMore: 'Read More',
      submit: 'Submit',
      sendMessage: 'Send Message',
      joinNow: 'Join Now',
      donateNow: 'Donate Now',
      viewAll: 'View All',
    },
    home: {
      hero: {
        title: 'Connecting Medical Professionals.',
        subtitle: 'Supporting Communities.',
        description:
          'A UK-based alumni and professional association supporting healthcare, networking, and charitable work across Khyber Pakhtunkhwa and beyond.',
      },
      whoWeAre: {
        title: 'Who We Are',
        p1: 'The Khyber Medical Association UK brings together alumni of Khyber Medical College and other medical graduates of Khyber Pakhtunkhwa origin living in the United Kingdom. Founded on the principles of professional excellence and humanitarian service, we have grown into a vibrant community of healthcare professionals dedicated to making a difference.',
        p2: 'Our focus areas include healthcare support, professional networking, humanitarian relief, and community engagement. We work to strengthen bonds among medical professionals while extending our expertise and resources to those who need it most.',
        statement:
          'The Association does not associate itself with any political, ethnic, or religious organisations.',
      },
      whatWeOffer: {
        title: 'What We Offer',
        subtitle:
          'Discover the benefits of membership, our charitable initiatives, and opportunities to connect.',
        membership: {
          title: 'Membership Benefits',
          description:
            'Join a prestigious network of medical professionals. Access exclusive events, mentorship programs, and professional development opportunities.',
        },
        charity: {
          title: 'Charity Projects',
          description:
            'From flood relief to medical camps, we bring healthcare and hope to underserved communities in Pakistan and beyond.',
        },
        social: {
          title: 'Social and Cultural Networking',
          description:
            'Connect with colleagues and families at our social gatherings, cultural events, and professional networking sessions.',
        },
      },
      recentImpact: {
        title: 'Recent Impact',
        subtitle:
          'See how our members are making a tangible difference in communities across Pakistan and the UK.',
        floodRelief: {
          title: 'Flood Relief Mission 2023',
          description:
            'Emergency medical aid and supplies delivered to flood-affected regions in KPK.',
        },
        eyeCamp: {
          title: 'Free Eye Camp',
          description:
            'Over 200 cataract surgeries performed for underserved communities.',
        },
        education: {
          title: 'Medical Education Fund',
          description:
            'Scholarships awarded to deserving medical students from KPK.',
        },
      },
    },
    about: {
      hero: {
        title: 'About KMA UK',
        subtitle:
          'A trusted community of medical professionals united by shared values, dedicated to excellence in healthcare and service to humanity.',
      },
      whoWeAre: {
        title: 'Who We Are',
        p1: 'The Khyber Medical Association UK is a professional and alumni organization that brings together medical graduates from Khyber Medical College and other healthcare professionals of Khyber Pakhtunkhwa origin residing in the United Kingdom.',
        p2: 'Established by a group of visionary doctors who recognized the need for a unified platform, we have grown into a respected organization known for our commitment to medical ethics, professional development, and humanitarian service.',
        p3: 'Our members include consultants, general practitioners, junior doctors, medical students, and allied health professionals, all connected by their roots in KPK and their dedication to making a positive impact in healthcare.',
      },
      independence: {
        title: 'Our Independence',
        description:
          'The Khyber Medical Association UK does not associate itself with any political, ethnic, or religious organisations. We are an independent professional body focused solely on healthcare excellence, education, and humanitarian service.',
      },
      purpose: {
        title: 'Our Purpose',
        subtitle:
          'We exist to strengthen the bonds among medical professionals while extending our expertise and resources to serve communities in need.',
        networking: {
          title: 'Professional Networking',
          description:
            "Creating opportunities for medical professionals to connect, collaborate, and support each other's career development.",
        },
        alumni: {
          title: 'Alumni Connection',
          description:
            'Maintaining strong ties with Khyber Medical College and fostering lifelong relationships among graduates.',
        },
        relief: {
          title: 'Humanitarian Relief',
          description:
            'Providing medical aid, disaster relief, and healthcare support to underserved communities in Pakistan.',
        },
        education: {
          title: 'Medical Education',
          description:
            'Supporting the next generation through scholarships, mentorship programs, and educational initiatives.',
        },
        community: {
          title: 'Community Engagement',
          description:
            'Organizing events, seminars, and social gatherings that strengthen our community bonds.',
        },
        ethics: {
          title: 'Medical Ethics',
          description:
            'Upholding the highest standards of medical practice and promoting ethical healthcare delivery.',
        },
      },
      values: {
        title: 'Our Values',
        subtitle:
          'These core principles guide everything we do, from professional practice to charitable work.',
        compassion: {
          title: 'Compassion',
          description:
            'We serve humanity with empathy and kindness, ensuring that healthcare reaches those who need it most, regardless of background.',
        },
        integrity: {
          title: 'Integrity',
          description:
            'We uphold the highest ethical standards in medical practice, maintaining transparency and accountability in all our endeavors.',
        },
        unity: {
          title: 'Unity',
          description:
            'We foster a supportive community that brings together medical professionals from diverse specialties and experiences.',
        },
        excellence: {
          title: 'Excellence',
          description:
            'We strive for the highest quality in medical practice, education, and humanitarian service.',
        },
      },
      vision: {
        title: 'Our Future Vision',
        p1: 'We envision a future where KMA UK serves as a model for professional medical associations worldwide—one that seamlessly blends career advancement with meaningful humanitarian impact.',
        p2: 'Our goals include expanding our charitable reach to more communities in Pakistan, establishing sustainable healthcare programs, creating more opportunities for young doctors through mentorship and scholarships, and strengthening the global network of KPK medical professionals.',
        p3: 'We are committed to leveraging technology to connect our members across continents, facilitating knowledge sharing, and ensuring that distance never diminishes our collective impact.',
        p4: 'Together, we are building a legacy of medical excellence and compassionate service that will benefit generations to come.',
      },
      journey: {
        title: 'Our Journey',
        subtitle: 'Key milestones in our history of service and dedication.',
      },
    },
    membership: {
      hero: {
        title: 'Join KMA UK',
        subtitle:
          'Become part of a distinguished network of medical professionals committed to excellence, community, and humanitarian service.',
      },
      yearly: {
        title: 'KMA UK Yearly Membership',
        subtitle: 'Annual commitment to our mission',
        price: '£30',
        period: 'per year',
        button: 'Join - Pay £30/year',
      },
      lifetime: {
        title: 'KMA UK Lifetime Membership',
        subtitle: 'Permanent commitment to our cause',
        price: '£300',
        period: 'one-off payment',
        button: 'Join - Pay £300 once',
        badge: 'Best Value',
      },
      benefits: 'Benefits Include:',
      yearlyBenefits: [
        'Full access to all KMA UK events and seminars',
        'Professional networking opportunities',
        'Voting rights in association elections',
        'Participation in charitable initiatives',
        'Exclusive member newsletter and updates',
        'Discounted rates for annual gala',
      ],
      lifetimeBenefits: [
        'All benefits of Yearly Membership',
        'Lifetime access with one-off payment',
        'Legacy member status recognition',
        'Priority registration for high-demand events',
        'No annual renewal required',
        'Permanent voting rights',
      ],
      nonRefundable: 'Please note: Membership fees are non-refundable.',
      whyJoin: {
        title: 'Why Join KMA UK?',
        subtitle:
          'Membership offers you a unique platform to connect, learn, and give back to communities in need.',
        network: {
          title: 'Professional Network',
          description:
            'Connect with senior consultants, GPs, and specialists across the UK.',
        },
        events: {
          title: 'Exclusive Annual Social Event',
          description:
            'Priority access to our prestigious annual gala dinner and social gatherings.',
        },
        impact: {
          title: 'Humanitarian Impact',
          description:
            'Participate in medical camps, disaster relief, and community health projects.',
        },
      },
      questions: {
        title: 'Have Questions?',
        subtitle:
          'If you have any questions about membership or need assistance with your application, our team is here to help.',
        button: 'Contact Membership Secretary',
      },
    },
    events: {
      hero: {
        title: 'Events & Activities',
        subtitle:
          'Join us for networking, learning, and family socializing within the KMA UK community. Regular yearly events will be shared as they are planned.',
      },
      upcoming: {
        title: 'Upcoming Events',
        subtitle: 'Mark your calendars for our upcoming gatherings.',
        spring: {
          title: "Members' Gathering",
          date: 'Spring 2026',
          location: 'To be announced',
          description: 'Details to follow.',
        },
        autumn: {
          title: 'AGM and Gala Event',
          date: 'Autumn 2026',
          location: 'To be announced',
          description: 'Details to follow.',
        },
      },
      past: {
        title: 'Past Events',
        subtitle:
          'A look back at our recent gatherings and community activities.',
      },
      cta: {
        title: "Don't Miss Out on Future Events",
        subtitle:
          'KMA UK members receive priority notification and exclusive access to all our events, seminars, and networking opportunities.',
        joinButton: 'Join KMA UK Today',
        learnButton: 'Learn More About Us',
      },
    },
    charity: {
      hero: {
        title: 'Serving Humanity',
        subtitle:
          'We believe healthcare is a fundamental human right. Our charity work brings relief, hope, and healing to those who need it most.',
      },
      past: {
        title: 'Past Charitable Activities',
        subtitle: 'A history of our commitment to serving communities in need.',
      },
      current: {
        title: 'Current Charitable Activities',
        subtitle:
          'Through the generous support of our members and donors, we run several ongoing projects focused on health, education, and community development.',
      },
      future: {
        title: 'Future Plans',
        subtitle:
          'Future charitable initiatives will be shared with members as they are confirmed.',
      },
      donate: {
        title: 'Make a Difference Today',
        subtitle:
          'Your contribution saves lives. 100% of your donation goes directly to our charitable projects. We maintain a strict zero-admin-fee policy for all donations.',
        button: 'Donate Now',
        stripeNote:
          'Secure payment via Stripe. All donations go to a single dedicated account.',
        bankTitle: 'Bank Transfer Details',
        bankName: 'Bank Name:',
        accountName: 'Account Name:',
        sortCode: 'Sort Code:',
        accountNo: 'Account No:',
        contactButton: 'Contact Us About Donations',
      },
    },
    team: {
      hero: {
        title: 'Our Leadership Team',
        subtitle:
          'Dedicated professionals volunteering their time and expertise to serve our community and advance our charitable mission.',
      },
      executive: {
        title: 'Executive Committee',
        subtitle:
          'Our executive team brings together decades of medical experience and a shared commitment to excellence and service.',
      },
      advisors: {
        title: 'Board of Advisors',
        subtitle:
          'Our advisors provide governance, oversight, and strategic guidance to ensure we fulfill our charitable objectives with integrity.',
      },
      charityNumber: 'Registered Charity Number: [To be confirmed]',
      contact: {
        title: 'Get in Touch with Our Team',
        subtitle:
          "Have questions about our work or interested in collaborating? We'd love to hear from you.",
        inquiries: 'General Inquiries',
      },
      fields: {
        specialty: 'Specialty:',
        workplace: 'Workplace:',
        experience: 'Experience:',
      },
    },
    contact: {
      hero: {
        title: 'Contact Us',
        subtitle:
          "We'd love to hear from you. Whether you have questions about membership, want to support our charity work, or just want to say hello.",
      },
      getInTouch: {
        title: 'Get in Touch',
        subtitle:
          "Whether you have a question about membership, want to donate, volunteer, or just want to learn more about our work, we're here to help.",
      },
      address: {
        title: 'Postal Address',
      },
      email: {
        title: 'Email Address',
      },
      privacy: {
        title: 'Your Privacy Matters',
        description:
          'We respect your privacy and will never share your information with third parties. All communications are handled securely and confidentially.',
        link: 'Read our Privacy Policy →',
      },
      form: {
        title: 'Send us a Message',
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email Address',
        subject: 'Subject',
        subjectPlaceholder: 'How can we help you?',
        message: 'Message',
        messagePlaceholder: 'Tell us more about your inquiry...',
        submit: 'Send Message',
        note: 'This form sends your message directly to info@kmauk.org',
      },
      quickLinks: {
        title: 'Looking for Something Specific?',
        membership: {
          title: 'Membership Inquiries',
          subtitle: 'Questions about joining KMA UK?',
          link: 'View Membership Options →',
        },
        donations: {
          title: 'Charitable Donations',
          subtitle: 'Want to support our projects?',
          link: 'Learn About Our Work →',
        },
        events: {
          title: 'Event Information',
          subtitle: 'Interested in upcoming events?',
          link: 'See Event Calendar →',
        },
      },
    },
    footer: {
      description:
        'A professional network of doctors and healthcare professionals dedicated to medical excellence and humanitarian service.',
      quickLinks: 'Quick Links',
      contactUs: 'Contact Us',
      connect: 'Connect',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
      copyright:
        '© {year} Khyber Medical Association UK. All rights reserved.',
      charity: 'Registered Charity in England & Wales.',
    },
  },
  ps: {
    nav: {
      home: 'کور',
      about: 'زموږ په اړه',
      membership: 'غړیتوب',
      events: 'پیښې',
      charity: 'خیرات',
      team: 'ټیم',
      contact: 'اړیکه',
      joinMembership: 'غړیتوب ترلاسه کړئ',
      donate: 'مرسته وکړئ',
      language: 'ژبه',
    },
    common: {
      learnMore: 'نور زده کړئ',
      viewProjects: 'پروژې وګورئ',
      seeEvents: 'پیښې وګورئ',
      contactUs: 'موږ سره اړیکه ونیسئ',
      readMore: 'نور ولولئ',
      submit: 'سپارل',
      sendMessage: 'پیغام ولیږئ',
      joinNow: 'اوس ګډون وکړئ',
      donateNow: 'اوس مرسته وکړئ',
      viewAll: 'ټول وګورئ',
    },
    home: {
      hero: {
        title: 'د طبي متخصصینو نښلول.',
        subtitle: 'د ټولنو ملاتړ.',
        description:
          'د انګلستان میشته فارغین او مسلکي ټولنه چې د خیبر پښتونخوا او نورو سیمو کې د روغتیا پاملرنې، شبکې او خیریه کارونو ملاتړ کوي.',
      },
      whoWeAre: {
        title: 'موږ څوک یو',
        p1: 'د خیبر طبي ټولنه انګلستان د خیبر طبي کالج فارغین او د خیبر پښتونخوا اصل نور طبي فارغین سره یوځای کوي چې په انګلستان کې اوسیږي.',
        p2: 'زموږ تمرکز ساحې د روغتیا پاملرنې ملاتړ، مسلکي شبکه، بشري مرستې او ټولنیز ښکیلتیا شاملوي.',
        statement:
          'دا ټولنه له هیڅ سیاسي، توکمیز یا مذهبي سازمان سره اړیکه نه لري.',
      },
      whatWeOffer: {
        title: 'موږ څه وړاندیز کوو',
        subtitle: 'د غړیتوب ګټې، زموږ خیریه نوښتونه او د نښلولو فرصتونه ومومئ.',
        membership: {
          title: 'د غړیتوب ګټې',
          description: 'د طبي متخصصینو یوې معتبرې شبکې سره یوځای شئ.',
        },
        charity: {
          title: 'خیریه پروژې',
          description:
            'له سیلاب مرستو څخه تر طبي کیمپونو پورې، موږ روغتیا پاملرنه او هیله وړاندې کوو.',
        },
        social: {
          title: 'ټولنیز او کلتوري شبکه',
          description:
            'زموږ ټولنیزو غونډو او کلتوري پیښو کې له همکارانو او کورنیو سره وصل شئ.',
        },
      },
      recentImpact: {
        title: 'وروستی اغیز',
        subtitle: 'وګورئ چې زموږ غړي څنګه په ټولنو کې توپیر رامنځته کوي.',
        floodRelief: {
          title: 'د سیلاب مرستې ماموریت ۲۰۲۳',
          description: 'بیړنۍ طبي مرسته او توکي سیلاب ځپلو سیمو ته ورسول شول.',
        },
        eyeCamp: {
          title: 'وړیا سترګو کیمپ',
          description: 'له ۲۰۰ څخه زیات د موتیابیند عملیات ترسره شول.',
        },
        education: {
          title: 'د طبي زده کړې فنډ',
          description: 'وړ طبي زده کونکو ته وظیفې ورکړل شوې.',
        },
      },
    },
    about: {
      hero: {
        title: 'د KMA UK په اړه',
        subtitle:
          'د طبي متخصصینو یوه باوري ټولنه چې د روغتیا پاملرنې کې عالیتوب ته وقف شوې.',
      },
      whoWeAre: {
        title: 'موږ څوک یو',
        p1: 'د خیبر طبي ټولنه انګلستان یوه مسلکي او فارغینو سازمان دی.',
        p2: 'د لید لرونکو ډاکټرانو لخوا تاسیس شوی.',
        p3: 'زموږ غړي مشاورین، عمومي پراکټیشنران، ځوان ډاکټران او طبي زده کونکي شامل دي.',
      },
      independence: {
        title: 'زموږ خپلواکي',
        description:
          'دا ټولنه له هیڅ سیاسي، توکمیز یا مذهبي سازمان سره اړیکه نه لري.',
      },
      purpose: {
        title: 'زموږ موخه',
        subtitle: 'موږ شتون لرو ترڅو د طبي متخصصینو ترمنځ اړیکې پیاوړې کړو.',
        networking: {
          title: 'مسلکي شبکه',
          description: 'د طبي متخصصینو لپاره د نښلولو فرصتونه رامنځته کول.',
        },
        alumni: {
          title: 'د فارغینو اړیکه',
          description: 'د خیبر طبي کالج سره قوي اړیکې ساتل.',
        },
        relief: {
          title: 'بشري مرستې',
          description: 'طبي مرسته او د ناورین مرسته چمتو کول.',
        },
        education: {
          title: 'طبي زده کړه',
          description: 'د راتلونکي نسل ملاتړ.',
        },
        community: {
          title: 'ټولنیز ښکیلتیا',
          description: 'پیښې او غونډې تنظیمول.',
        },
        ethics: {
          title: 'طبي اخلاق',
          description: 'لوړ معیارونه ساتل.',
        },
      },
      values: {
        title: 'زموږ ارزښتونه',
        subtitle: 'دا اصلي اصول زموږ ټول کارونه لارښوونه کوي.',
        compassion: {
          title: 'زړه سوزي',
          description: 'موږ بشریت ته د خواخوږۍ سره خدمت کوو.',
        },
        integrity: {
          title: 'صداقت',
          description: 'موږ لوړ اخلاقي معیارونه ساتو.',
        },
        unity: {
          title: 'یووالی',
          description: 'موږ یوه ملاتړونکې ټولنه وده کوو.',
        },
        excellence: {
          title: 'عالیتوب',
          description: 'موږ لوړ کیفیت ته هڅه کوو.',
        },
      },
      vision: {
        title: 'زموږ راتلونکی لید',
        p1: 'موږ داسې راتلونکی تصور کوو چیرې چې KMA UK د مسلکي طبي ټولنو لپاره یو نمونه وي.',
        p2: 'زموږ موخې د خیریه رسیدو پراخول شامل دي.',
        p3: 'موږ ژمن یو چې ټیکنالوژي وکاروو.',
        p4: 'یوځای، موږ د طبي عالیتوب میراث جوړوو.',
      },
      journey: {
        title: 'زموږ سفر',
        subtitle: 'د خدمت او وقف په تاریخ کې مهم پړاوونه.',
      },
    },
    membership: {
      hero: {
        title: 'KMA UK سره یوځای شئ',
        subtitle: 'د طبي متخصصینو یوې معتبرې شبکې برخه شئ.',
      },
      yearly: {
        title: 'KMA UK کلنی غړیتوب',
        subtitle: 'زموږ ماموریت ته کلنی ژمنتیا',
        price: '£۳۰',
        period: 'په کال کې',
        button: 'ګډون - £۳۰/کال ورکړئ',
      },
      lifetime: {
        title: 'KMA UK ټول عمر غړیتوب',
        subtitle: 'زموږ هدف ته دایمي ژمنتیا',
        price: '£۳۰۰',
        period: 'یو ځل تادیه',
        button: 'ګډون - £۳۰۰ یو ځل ورکړئ',
        badge: 'غوره ارزښت',
      },
      benefits: 'ګټې شاملې دي:',
      yearlyBenefits: [
        'ټولو KMA UK پیښو ته بشپړ لاسرسی',
        'مسلکي شبکې فرصتونه',
        'په ټولنیزو ټاکنو کې د رایې ورکولو حقونه',
        'په خیریه نوښتونو کې ګډون',
        'ځانګړی غړي خبرپاڼه',
        'د کلنۍ ګالا لپاره کم نرخونه',
      ],
      lifetimeBenefits: [
        'د کلني غړیتوب ټولې ګټې',
        'د یو ځل تادیې سره ټول عمر لاسرسی',
        'د میراث غړي حیثیت پیژندنه',
        'د لوړ تقاضا پیښو لپاره لومړیتوب ثبت',
        'کلنی نوي کول اړین نه دي',
        'دایمي رایې ورکولو حقونه',
      ],
      nonRefundable:
        'مهرباني وکړئ یادونه وکړئ: د غړیتوب فیسونه بیرته نه راستنیږي.',
      whyJoin: {
        title: 'ولې KMA UK سره یوځای شئ؟',
        subtitle:
          'غړیتوب تاسو ته د نښلولو، زده کړې او ټولنو ته مرستې لپاره یو ځانګړی پلیټ فارم وړاندې کوي.',
        network: {
          title: 'مسلکي شبکه',
          description: 'په ټول انګلستان کې له لوړ پوړو مشاورینو سره وصل شئ.',
        },
        events: {
          title: 'ځانګړې کلنۍ ټولنیزه پیښه',
          description: 'زموږ معتبر کلني ګالا ډوډۍ ته لومړیتوب لاسرسی.',
        },
        impact: {
          title: 'بشري اغیز',
          description: 'په طبي کیمپونو او د ناورین مرستو کې ګډون وکړئ.',
        },
      },
      questions: {
        title: 'پوښتنې لرئ؟',
        subtitle:
          'که تاسو د غړیتوب په اړه پوښتنې لرئ، زموږ ټیم د مرستې لپاره دلته دی.',
        button: 'د غړیتوب سکرتر سره اړیکه ونیسئ',
      },
    },
    events: {
      hero: {
        title: 'پیښې او فعالیتونه',
        subtitle:
          'د KMA UK ټولنې کې د شبکې، زده کړې او کورنۍ ټولنیز کولو لپاره موږ سره یوځای شئ.',
      },
      upcoming: {
        title: 'راتلونکې پیښې',
        subtitle: 'زموږ راتلونکو غونډو لپاره خپل کلیزې نښه کړئ.',
        spring: {
          title: 'د غړو غونډه',
          date: 'پسرلی ۲۰۲۶',
          location: 'اعلان کیږي',
          description: 'توضیحات تعقیبیږي.',
        },
        autumn: {
          title: 'AGM او ګالا پیښه',
          date: 'منی ۲۰۲۶',
          location: 'اعلان کیږي',
          description: 'توضیحات تعقیبیږي.',
        },
      },
      past: {
        title: 'تیرې پیښې',
        subtitle: 'زموږ وروستیو غونډو او ټولنیزو فعالیتونو ته یوه کتنه.',
      },
      cta: {
        title: 'راتلونکې پیښې له لاسه مه ورکوئ',
        subtitle:
          'KMA UK غړي ټولو پیښو ته لومړیتوب خبرتیا او ځانګړی لاسرسی ترلاسه کوي.',
        joinButton: 'نن KMA UK سره یوځای شئ',
        learnButton: 'زموږ په اړه نور زده کړئ',
      },
    },
    charity: {
      hero: {
        title: 'بشریت ته خدمت',
        subtitle: 'موږ باور لرو چې روغتیا پاملرنه یو بنسټیز بشري حق دی.',
      },
      past: {
        title: 'تیرې خیریه فعالیتونه',
        subtitle: 'اړمنو ټولنو ته د خدمت لپاره زموږ ژمنتیا تاریخ.',
      },
      current: {
        title: 'اوسني خیریه فعالیتونه',
        subtitle: 'زموږ غړو او مرسته کونکو له سخاوتمندانه ملاتړ سره.',
      },
      future: {
        title: 'راتلونکي پلانونه',
        subtitle:
          'راتلونکي خیریه نوښتونه به د غړو سره شریک شي کله چې تایید شي.',
      },
      donate: {
        title: 'نن توپیر رامنځته کړئ',
        subtitle:
          'ستاسو مرسته ژوندونه ژغوري. ستاسو د مرستې ۱۰۰٪ مستقیم خیریه پروژو ته ځي.',
        button: 'اوس مرسته وکړئ',
        stripeNote: 'د Stripe له لارې خوندي تادیه.',
        bankTitle: 'د بانک لیږد توضیحات',
        bankName: 'د بانک نوم:',
        accountName: 'د حساب نوم:',
        sortCode: 'ترتیب کوډ:',
        accountNo: 'د حساب شمیره:',
        contactButton: 'د مرستو په اړه موږ سره اړیکه ونیسئ',
      },
    },
    team: {
      hero: {
        title: 'زموږ مشري ټیم',
        subtitle: 'وقف شوي متخصصین چې خپل وخت او تخصص رضاکارانه کوي.',
      },
      executive: {
        title: 'اجرایوي کمیټه',
        subtitle: 'زموږ اجرایوي ټیم لسیزې طبي تجربه راوړي.',
      },
      advisors: {
        title: 'د مشاورینو بورډ',
        subtitle: 'زموږ مشاورین حکومتداري او ستراتیژیک لارښوونه چمتو کوي.',
      },
      charityNumber: 'ثبت شوی خیریه شمیره: [تایید کیږي]',
      contact: {
        title: 'زموږ ټیم سره اړیکه ونیسئ',
        subtitle:
          'زموږ کار په اړه پوښتنې لرئ؟ موږ به خوښ شو چې له تاسو څخه واورو.',
        inquiries: 'عمومي پوښتنې',
      },
      fields: {
        specialty: 'تخصص:',
        workplace: 'کاري ځای:',
        experience: 'تجربه:',
      },
    },
    contact: {
      hero: {
        title: 'موږ سره اړیکه ونیسئ',
        subtitle: 'موږ به خوښ شو چې له تاسو څخه واورو.',
      },
      getInTouch: {
        title: 'اړیکه ونیسئ',
        subtitle: 'موږ دلته یو چې مرسته وکړو.',
      },
      address: {
        title: 'پوستي پته',
      },
      email: {
        title: 'بریښنالیک پته',
      },
      privacy: {
        title: 'ستاسو محرمیت مهم دی',
        description:
          'موږ ستاسو محرمیت ته درناوی کوو او هیڅکله به ستاسو معلومات دریمو اړخونو سره شریک نه کړو.',
        link: 'زموږ د محرمیت پالیسي ولولئ →',
      },
      form: {
        title: 'موږ ته پیغام ولیږئ',
        firstName: 'لومړی نوم',
        lastName: 'تخلص',
        email: 'بریښنالیک پته',
        subject: 'موضوع',
        subjectPlaceholder: 'موږ څنګه مرسته کولی شو؟',
        message: 'پیغام',
        messagePlaceholder: 'زموږ ته د خپلې پوښتنې په اړه نور ووایاست...',
        submit: 'پیغام ولیږئ',
        note: 'دا فورمه ستاسو پیغام مستقیم info@kmauk.org ته لیږي',
      },
      quickLinks: {
        title: 'ځانګړی څه لټوئ؟',
        membership: {
          title: 'د غړیتوب پوښتنې',
          subtitle: 'د KMA UK سره د یوځای کیدو پوښتنې؟',
          link: 'د غړیتوب اختیارونه وګورئ →',
        },
        donations: {
          title: 'خیریه مرستې',
          subtitle: 'غواړئ زموږ پروژو ته مرسته وکړئ؟',
          link: 'زموږ کار په اړه زده کړئ →',
        },
        events: {
          title: 'د پیښو معلومات',
          subtitle: 'راتلونکو پیښو کې لیوالتیا لرئ؟',
          link: 'د پیښو کلیزه وګورئ →',
        },
      },
    },
    footer: {
      description: 'د ډاکټرانو او روغتیا پاملرنې متخصصینو مسلکي شبکه.',
      quickLinks: 'چټک لینکونه',
      contactUs: 'موږ سره اړیکه ونیسئ',
      connect: 'وصل شئ',
      privacyPolicy: 'د محرمیت پالیسي',
      termsOfService: 'د خدمت شرایط',
      copyright: '© {year} خیبر طبي ټولنه انګلستان. ټول حقونه خوندي دي.',
      charity: 'په انګلستان او ویلز کې ثبت شوی خیریه.',
    },
  },
  ur: {
    nav: {
      home: 'ہوم',
      about: 'ہمارے بارے میں',
      membership: 'رکنیت',
      events: 'تقریبات',
      charity: 'خیرات',
      team: 'ٹیم',
      contact: 'رابطہ',
      joinMembership: 'رکنیت حاصل کریں',
      donate: 'عطیہ کریں',
      language: 'زبان',
    },
    common: {
      learnMore: 'مزید جانیں',
      viewProjects: 'پروجیکٹس دیکھیں',
      seeEvents: 'تقریبات دیکھیں',
      contactUs: 'ہم سے رابطہ کریں',
      readMore: 'مزید پڑھیں',
      submit: 'جمع کرائیں',
      sendMessage: 'پیغام بھیجیں',
      joinNow: 'ابھی شامل ہوں',
      donateNow: 'ابھی عطیہ کریں',
      viewAll: 'سب دیکھیں',
    },
    home: {
      hero: {
        title: 'طبی پیشہ ور افراد کو جوڑنا۔',
        subtitle: 'کمیونٹیز کی مدد۔',
        description:
          'برطانیہ میں مقیم سابق طلباء اور پیشہ ور ایسوسی ایشن جو خیبر پختونخوا اور اس سے آگے صحت کی دیکھ بھال، نیٹ ورکنگ اور خیراتی کام کی حمایت کرتی ہے۔',
      },
      whoWeAre: {
        title: 'ہم کون ہیں',
        p1: 'خیبر میڈیکل ایسوسی ایشن یوکے خیبر میڈیکل کالج کے سابق طلباء اور خیبر پختونخوا سے تعلق رکھنے والے دیگر طبی فارغ التحصیل افراد کو اکٹھا کرتی ہے جو برطانیہ میں رہتے ہیں۔',
        p2: 'ہماری توجہ کے شعبوں میں صحت کی دیکھ بھال کی حمایت، پیشہ ورانہ نیٹ ورکنگ، انسانی امداد اور کمیونٹی مشغولیت شامل ہے۔',
        statement:
          'یہ ایسوسی ایشن کسی بھی سیاسی، نسلی یا مذہبی تنظیم سے وابستہ نہیں ہے۔',
      },
      whatWeOffer: {
        title: 'ہم کیا پیش کرتے ہیں',
        subtitle:
          'رکنیت کے فوائد، ہمارے خیراتی اقدامات اور جڑنے کے مواقع دریافت کریں۔',
        membership: {
          title: 'رکنیت کے فوائد',
          description: 'طبی پیشہ ور افراد کے ایک معزز نیٹ ورک میں شامل ہوں۔',
        },
        charity: {
          title: 'خیراتی پروجیکٹس',
          description:
            'سیلاب امداد سے لے کر طبی کیمپس تک، ہم صحت کی دیکھ بھال اور امید فراہم کرتے ہیں۔',
        },
        social: {
          title: 'سماجی اور ثقافتی نیٹ ورکنگ',
          description:
            'ہماری سماجی تقریبات اور ثقافتی پروگراموں میں ساتھیوں اور خاندانوں سے جڑیں۔',
        },
      },
      recentImpact: {
        title: 'حالیہ اثر',
        subtitle:
          'دیکھیں کہ ہمارے اراکین کمیونٹیز میں کیسے فرق پیدا کر رہے ہیں۔',
        floodRelief: {
          title: 'سیلاب امداد مشن 2023',
          description:
            'سیلاب سے متاثرہ علاقوں میں ہنگامی طبی امداد اور سامان پہنچایا گیا۔',
        },
        eyeCamp: {
          title: 'مفت آنکھوں کا کیمپ',
          description: '200 سے زیادہ موتیا کی سرجریاں کی گئیں۔',
        },
        education: {
          title: 'طبی تعلیم فنڈ',
          description: 'مستحق طبی طلباء کو وظائف دیے گئے۔',
        },
      },
    },
    about: {
      hero: {
        title: 'KMA UK کے بارے میں',
        subtitle:
          'طبی پیشہ ور افراد کی ایک قابل اعتماد کمیونٹی جو صحت کی دیکھ بھال میں عمدگی کے لیے وقف ہے۔',
      },
      whoWeAre: {
        title: 'ہم کون ہیں',
        p1: 'خیبر میڈیکل ایسوسی ایشن یوکے ایک پیشہ ورانہ اور سابق طلباء کی تنظیم ہے۔',
        p2: 'دور اندیش ڈاکٹروں کے ایک گروپ نے قائم کیا۔',
        p3: 'ہمارے اراکین میں کنسلٹنٹس، جنرل پریکٹیشنرز، جونیئر ڈاکٹرز اور طبی طلباء شامل ہیں۔',
      },
      independence: {
        title: 'ہماری آزادی',
        description:
          'یہ ایسوسی ایشن کسی بھی سیاسی، نسلی یا مذہبی تنظیم سے وابستہ نہیں ہے۔',
      },
      purpose: {
        title: 'ہمارا مقصد',
        subtitle:
          'ہم طبی پیشہ ور افراد کے درمیان تعلقات کو مضبوط بنانے کے لیے موجود ہیں۔',
        networking: {
          title: 'پیشہ ورانہ نیٹ ورکنگ',
          description: 'طبی پیشہ ور افراد کے لیے جڑنے کے مواقع پیدا کرنا۔',
        },
        alumni: {
          title: 'سابق طلباء کا رابطہ',
          description: 'خیبر میڈیکل کالج کے ساتھ مضبوط تعلقات برقرار رکھنا۔',
        },
        relief: {
          title: 'انسانی امداد',
          description: 'طبی امداد اور آفات سے نجات فراہم کرنا۔',
        },
        education: {
          title: 'طبی تعلیم',
          description: 'اگلی نسل کی مدد کرنا۔',
        },
        community: {
          title: 'کمیونٹی مشغولیت',
          description: 'تقریبات اور اجتماعات کا انعقاد۔',
        },
        ethics: {
          title: 'طبی اخلاقیات',
          description: 'اعلیٰ معیارات کو برقرار رکھنا۔',
        },
      },
      values: {
        title: 'ہماری اقدار',
        subtitle: 'یہ بنیادی اصول ہمارے تمام کاموں کی رہنمائی کرتے ہیں۔',
        compassion: {
          title: 'ہمدردی',
          description: 'ہم انسانیت کی ہمدردی کے ساتھ خدمت کرتے ہیں۔',
        },
        integrity: {
          title: 'دیانتداری',
          description: 'ہم اعلیٰ اخلاقی معیارات کو برقرار رکھتے ہیں۔',
        },
        unity: {
          title: 'اتحاد',
          description: 'ہم ایک معاون کمیونٹی کو فروغ دیتے ہیں۔',
        },
        excellence: {
          title: 'عمدگی',
          description: 'ہم اعلیٰ معیار کے لیے کوشش کرتے ہیں۔',
        },
      },
      vision: {
        title: 'ہمارا مستقبل کا وژن',
        p1: 'ہم ایسے مستقبل کا تصور کرتے ہیں جہاں KMA UK پیشہ ورانہ طبی ایسوسی ایشنز کے لیے ایک نمونہ ہو۔',
        p2: 'ہمارے اہداف میں خیراتی رسائی کو بڑھانا شامل ہے۔',
        p3: 'ہم ٹیکنالوجی استعمال کرنے کے لیے پرعزم ہیں۔',
        p4: 'مل کر، ہم طبی عمدگی کی میراث بنا رہے ہیں۔',
      },
      journey: {
        title: 'ہمارا سفر',
        subtitle: 'خدمت اور لگن کی تاریخ میں اہم سنگ میل۔',
      },
    },
    membership: {
      hero: {
        title: 'KMA UK میں شامل ہوں',
        subtitle: 'طبی پیشہ ور افراد کے ایک معزز نیٹ ورک کا حصہ بنیں۔',
      },
      yearly: {
        title: 'KMA UK سالانہ رکنیت',
        subtitle: 'ہمارے مشن کے لیے سالانہ عزم',
        price: '£30',
        period: 'فی سال',
        button: 'شامل ہوں - £30/سال ادا کریں',
      },
      lifetime: {
        title: 'KMA UK تاحیات رکنیت',
        subtitle: 'ہمارے مقصد کے لیے مستقل عزم',
        price: '£300',
        period: 'ایک بار ادائیگی',
        button: 'شامل ہوں - £300 ایک بار ادا کریں',
        badge: 'بہترین قیمت',
      },
      benefits: 'فوائد شامل ہیں:',
      yearlyBenefits: [
        'تمام KMA UK تقریبات تک مکمل رسائی',
        'پیشہ ورانہ نیٹ ورکنگ کے مواقع',
        'ایسوسی ایشن انتخابات میں ووٹنگ کے حقوق',
        'خیراتی اقدامات میں شرکت',
        'خصوصی ممبر نیوز لیٹر',
        'سالانہ گالا کے لیے رعایتی نرخ',
      ],
      lifetimeBenefits: [
        'سالانہ رکنیت کے تمام فوائد',
        'ایک بار ادائیگی کے ساتھ تاحیات رسائی',
        'میراثی رکن کی حیثیت کی پہچان',
        'زیادہ مانگ والی تقریبات کے لیے ترجیحی رجسٹریشن',
        'سالانہ تجدید کی ضرورت نہیں',
        'مستقل ووٹنگ کے حقوق',
      ],
      nonRefundable: 'براہ کرم نوٹ کریں: رکنیت کی فیس واپس نہیں ہوتی۔',
      whyJoin: {
        title: 'KMA UK میں کیوں شامل ہوں؟',
        subtitle:
          'رکنیت آپ کو جڑنے، سیکھنے اور کمیونٹیز کی مدد کرنے کا ایک منفرد پلیٹ فارم فراہم کرتی ہے۔',
        network: {
          title: 'پیشہ ورانہ نیٹ ورک',
          description: 'پورے برطانیہ میں سینئر کنسلٹنٹس سے جڑیں۔',
        },
        events: {
          title: 'خصوصی سالانہ سماجی تقریب',
          description: 'ہماری معزز سالانہ گالا ڈنر تک ترجیحی رسائی۔',
        },
        impact: {
          title: 'انسانی اثر',
          description: 'طبی کیمپس اور آفات سے نجات میں حصہ لیں۔',
        },
      },
      questions: {
        title: 'سوالات ہیں؟',
        subtitle:
          'اگر آپ کے رکنیت کے بارے میں سوالات ہیں، ہماری ٹیم مدد کے لیے یہاں ہے۔',
        button: 'رکنیت سیکرٹری سے رابطہ کریں',
      },
    },
    events: {
      hero: {
        title: 'تقریبات اور سرگرمیاں',
        subtitle:
          'KMA UK کمیونٹی میں نیٹ ورکنگ، سیکھنے اور خاندانی سماجی میل جول کے لیے ہمارے ساتھ شامل ہوں۔',
      },
      upcoming: {
        title: 'آنے والی تقریبات',
        subtitle: 'ہماری آنے والی تقریبات کے لیے اپنے کیلنڈر نشان زد کریں۔',
        spring: {
          title: 'اراکین کا اجتماع',
          date: 'بہار 2026',
          location: 'اعلان کیا جائے گا',
          description: 'تفصیلات بعد میں۔',
        },
        autumn: {
          title: 'AGM اور گالا تقریب',
          date: 'خزاں 2026',
          location: 'اعلان کیا جائے گا',
          description: 'تفصیلات بعد میں۔',
        },
      },
      past: {
        title: 'گزشتہ تقریبات',
        subtitle: 'ہماری حالیہ تقریبات اور کمیونٹی سرگرمیوں پر ایک نظر۔',
      },
      cta: {
        title: 'آنے والی تقریبات سے محروم نہ رہیں',
        subtitle:
          'KMA UK اراکین تمام تقریبات تک ترجیحی اطلاع اور خصوصی رسائی حاصل کرتے ہیں۔',
        joinButton: 'آج ہی KMA UK میں شامل ہوں',
        learnButton: 'ہمارے بارے میں مزید جانیں',
      },
    },
    charity: {
      hero: {
        title: 'انسانیت کی خدمت',
        subtitle:
          'ہم یقین رکھتے ہیں کہ صحت کی دیکھ بھال ایک بنیادی انسانی حق ہے۔',
      },
      past: {
        title: 'گزشتہ خیراتی سرگرمیاں',
        subtitle: 'ضرورت مند کمیونٹیز کی خدمت کے لیے ہماری وابستگی کی تاریخ۔',
      },
      current: {
        title: 'موجودہ خیراتی سرگرمیاں',
        subtitle: 'ہمارے اراکین اور عطیہ دہندگان کی فراخ دلانہ حمایت سے۔',
      },
      future: {
        title: 'مستقبل کے منصوبے',
        subtitle:
          'مستقبل کے خیراتی اقدامات اراکین کے ساتھ شیئر کیے جائیں گے جب تصدیق ہو جائے۔',
      },
      donate: {
        title: 'آج فرق پیدا کریں',
        subtitle:
          'آپ کا عطیہ زندگیاں بچاتا ہے۔ آپ کے عطیے کا 100% براہ راست خیراتی پروجیکٹس میں جاتا ہے۔',
        button: 'ابھی عطیہ کریں',
        stripeNote: 'Stripe کے ذریعے محفوظ ادائیگی۔',
        bankTitle: 'بینک ٹرانسفر کی تفصیلات',
        bankName: 'بینک کا نام:',
        accountName: 'اکاؤنٹ کا نام:',
        sortCode: 'سورٹ کوڈ:',
        accountNo: 'اکاؤنٹ نمبر:',
        contactButton: 'عطیات کے بارے میں ہم سے رابطہ کریں',
      },
    },
    team: {
      hero: {
        title: 'ہماری قیادت کی ٹیم',
        subtitle:
          'وقف پیشہ ور افراد جو اپنا وقت اور مہارت رضاکارانہ طور پر دیتے ہیں۔',
      },
      executive: {
        title: 'ایگزیکٹو کمیٹی',
        subtitle: 'ہماری ایگزیکٹو ٹیم دہائیوں کا طبی تجربہ لاتی ہے۔',
      },
      advisors: {
        title: 'مشیروں کا بورڈ',
        subtitle: 'ہمارے مشیر گورننس اور اسٹریٹجک رہنمائی فراہم کرتے ہیں۔',
      },
      charityNumber: 'رجسٹرڈ چیریٹی نمبر: [تصدیق ہونی ہے]',
      contact: {
        title: 'ہماری ٹیم سے رابطہ کریں',
        subtitle:
          'ہمارے کام کے بارے میں سوالات ہیں؟ ہمیں آپ سے سننا اچھا لگے گا۔',
        inquiries: 'عمومی پوچھ گچھ',
      },
      fields: {
        specialty: 'تخصص:',
        workplace: 'کام کی جگہ:',
        experience: 'تجربہ:',
      },
    },
    contact: {
      hero: {
        title: 'ہم سے رابطہ کریں',
        subtitle: 'ہمیں آپ سے سننا اچھا لگے گا۔',
      },
      getInTouch: {
        title: 'رابطہ کریں',
        subtitle: 'ہم مدد کے لیے یہاں ہیں۔',
      },
      address: {
        title: 'ڈاک کا پتہ',
      },
      email: {
        title: 'ای میل پتہ',
      },
      privacy: {
        title: 'آپ کی رازداری اہم ہے',
        description:
          'ہم آپ کی رازداری کا احترام کرتے ہیں اور کبھی بھی آپ کی معلومات تیسرے فریق کے ساتھ شیئر نہیں کریں گے۔',
        link: 'ہماری رازداری کی پالیسی پڑھیں →',
      },
      form: {
        title: 'ہمیں پیغام بھیجیں',
        firstName: 'پہلا نام',
        lastName: 'آخری نام',
        email: 'ای میل پتہ',
        subject: 'موضوع',
        subjectPlaceholder: 'ہم کیسے مدد کر سکتے ہیں؟',
        message: 'پیغام',
        messagePlaceholder: 'ہمیں اپنی پوچھ گچھ کے بارے میں مزید بتائیں...',
        submit: 'پیغام بھیجیں',
        note: 'یہ فارم آپ کا پیغام براہ راست info@kmauk.org پر بھیجتا ہے',
      },
      quickLinks: {
        title: 'کچھ خاص تلاش کر رہے ہیں؟',
        membership: {
          title: 'رکنیت کی پوچھ گچھ',
          subtitle: 'KMA UK میں شامل ہونے کے بارے میں سوالات؟',
          link: 'رکنیت کے اختیارات دیکھیں →',
        },
        donations: {
          title: 'خیراتی عطیات',
          subtitle: 'ہمارے پروجیکٹس کی مدد کرنا چاہتے ہیں؟',
          link: 'ہمارے کام کے بارے میں جانیں →',
        },
        events: {
          title: 'تقریب کی معلومات',
          subtitle: 'آنے والی تقریبات میں دلچسپی ہے؟',
          link: 'تقریب کا کیلنڈر دیکھیں →',
        },
      },
    },
    footer: {
      description:
        'ڈاکٹروں اور صحت کی دیکھ بھال کے پیشہ ور افراد کا پیشہ ورانہ نیٹ ورک۔',
      quickLinks: 'فوری لنکس',
      contactUs: 'ہم سے رابطہ کریں',
      connect: 'جڑیں',
      privacyPolicy: 'رازداری کی پالیسی',
      termsOfService: 'سروس کی شرائط',
      copyright: '© {year} خیبر میڈیکل ایسوسی ایشن یوکے۔ جملہ حقوق محفوظ ہیں۔',
      charity: 'انگلینڈ اور ویلز میں رجسٹرڈ چیریٹی۔',
    },
  },
}

export type TranslationKeys = typeof translations.en
